import type { RecommendationCampaignRow } from '@/types/campaignRecommendations';

export const DEFAULT_BUDGET_INCREASE_PCT = 20;

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function effectiveBudgetIncreasePct(pct: number | undefined): number {
  if (pct == null || !Number.isFinite(pct) || pct <= 0) return DEFAULT_BUDGET_INCREASE_PCT;
  return pct;
}

/** Extra earnings if budget (+pct%) scales spend at constant ROAS (sales/cost). */
export function computeHighPerformerProjections(
  cost: number,
  sales: number,
  budgetIncreasePct: number
): Pick<
  RecommendationCampaignRow,
  | 'budgetIncreasePct'
  | 'projectedAdditionalCost'
  | 'projectedAdditionalSales'
  | 'projectedAdditionalEarnings'
> {
  const pct = effectiveBudgetIncreasePct(budgetIncreasePct);
  if (cost <= 0) {
    return {
      budgetIncreasePct: pct,
      projectedAdditionalCost: 0,
      projectedAdditionalSales: 0,
      projectedAdditionalEarnings: 0,
    };
  }
  const incrementalCost = cost * (pct / 100);
  const roas = sales / cost;
  const incrementalSales = incrementalCost * roas;
  const incrementalEarnings = incrementalSales - incrementalCost;
  return {
    budgetIncreasePct: pct,
    projectedAdditionalCost: roundMoney(incrementalCost),
    projectedAdditionalSales: roundMoney(incrementalSales),
    projectedAdditionalEarnings: roundMoney(incrementalEarnings),
  };
}

export function computeLowPerformerProjections(
  cost: number,
  sales: number
): Pick<RecommendationCampaignRow, 'projectedSavings' | 'projectedLossAvoided'> {
  return {
    projectedSavings: roundMoney(cost),
    projectedLossAvoided: roundMoney(Math.max(cost - sales, 0)),
  };
}

export function enrichHighPerformerRow(
  row: RecommendationCampaignRow,
  defaultPct: number
): RecommendationCampaignRow {
  const projections = computeHighPerformerProjections(row.cost, row.sales30d, defaultPct);
  return { ...row, ...projections };
}

export function enrichLowPerformerRow(row: RecommendationCampaignRow): RecommendationCampaignRow {
  const projections = computeLowPerformerProjections(row.cost, row.sales30d);
  return { ...row, ...projections };
}

export function sortHighPerformersByEarnings(
  rows: RecommendationCampaignRow[]
): RecommendationCampaignRow[] {
  return [...rows].sort(
    (a, b) => (b.projectedAdditionalEarnings ?? 0) - (a.projectedAdditionalEarnings ?? 0)
  );
}

export function sortLowPerformersBySavings(
  rows: RecommendationCampaignRow[]
): RecommendationCampaignRow[] {
  return [...rows].sort((a, b) => (b.projectedSavings ?? 0) - (a.projectedSavings ?? 0));
}
